import { useCallback, useMemo } from 'react';
import {
  type BaseLocationHook,
  useLocation,
  useRoute,
  useSearch,
} from 'wouter';
import { useBrowserLocation } from 'wouter/use-browser-location';

import { useLanguage } from 'store';

type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue | QueryValue[]>;
type SetQuery = (query: Query) => void;

export const notAuthPathRegexp = /^(?!\/(signin|signup|reset-password)(\/|$))/;
export const notGamePagePathRegexp = /^(?!\/game\/(?<gameId>\d+)\/?$)/;
export const casinoPathsRegexp =
  /^\/((live-|)casino(\/favorites)?|game(|-providers)(\/.+)?)\/?$/;
export const gamePagePathRegexp = /^\/game\/(?<gameId>\d+)\/?$/;
export const casinoPagePathRegexp =
  /\/(?<group>(live-|)casino)(?:\/(?<subpage>favorites))?\/?$/;
export const paymentPagePathRegexp =
  /^\/payment\/?(?<method>deposit|withdrawal)?\/?(?<activeMethodId>\d+)?\/?$/;

const getQueryValues = (query: Query, key: string): QueryValue[] => {
  const value = query[key];
  if (value === undefined || value === null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
};

export const useSearchQuery: () => [URLSearchParams, setQuery: SetQuery] =
  () => {
    const search = useSearch();
    const [location, navigate] = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    const setQuery = useCallback<SetQuery>(
      (query) => {
        for (const key in query) {
          if (searchParams.has(key)) {
            searchParams.delete(key);
          }
          const values = getQueryValues(query, key);

          for (const value of values) {
            searchParams.append(key, String(value));
          }
        }

        navigate(`${location}?${searchParams}`);
      },
      [searchParams, navigate, location],
    );

    return [searchParams, setQuery];
  };

type BasicRouteName =
  | 'main'
  | 'casino'
  | 'sportsbook'
  | 'account'
  | 'static'
  | 'auth';

export const useBasicRoutes = (): [
  BasicRouteName,
  {
    isMain: boolean;
    isCasino: boolean;
    isSportsbook: boolean;
    isAccount: boolean;
    isAuth: boolean;
    isStatic: boolean;
  },
] => {
  const [mainPage] = useRoute('/');
  const [casinoPage] = useRoute('/((live-|)casino|game(|-providers))/*?');
  const [sportsbookPage] = useRoute('/sportsbook/*?');
  const [accountPage] = useRoute('/account/*?');
  const [authPage] = useRoute('/(signin|signup|reset-password)');

  const basicRoutesMap = {
    isMain: !!mainPage,
    isCasino: !!casinoPage,
    isSportsbook: !!sportsbookPage,
    isAccount: !!accountPage,
    isAuth: !!authPage,
    isStatic: !(
      mainPage ||
      casinoPage ||
      sportsbookPage ||
      accountPage ||
      authPage
    ),
  };

  if (basicRoutesMap.isMain) {
    return ['main', basicRoutesMap];
  }
  if (basicRoutesMap.isCasino) {
    return ['casino', basicRoutesMap];
  }
  if (basicRoutesMap.isSportsbook) {
    return ['sportsbook', basicRoutesMap];
  }
  if (basicRoutesMap.isAccount) {
    return ['account', basicRoutesMap];
  }
  if (basicRoutesMap.isAuth) {
    return ['auth', basicRoutesMap];
  }
  return ['static', basicRoutesMap];
};

export const useCustomLocation: BaseLocationHook = () => {
  const [location, setLocation] = useBrowserLocation();
  const [{ basePath }] = useLanguage();

  const navigate = useCallback<typeof setLocation>(
    (to, options) => {
      const toStringified = to.toString();
      const isExternal = /:\/{2}/.test(toStringified);

      if (isExternal) {
        window.location.href = toStringified;
        return;
      }

      const target = toStringified.startsWith(basePath)
        ? toStringified
        : `${basePath}${toStringified}`;

      setLocation(target, options);
    },
    [setLocation, basePath],
  );

  return [location, navigate];
};
